// src/components/roles/RoleList.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchRoles, deleteRole, clearRoleError, clearRoleSuccess } from '../../store/actions/roleActions';
import Pagination from '../common/Pagination';
import SearchBar from '../common/SearchBar';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';

const RoleList = () => {
  const dispatch = useDispatch();
  const { roles, loading, error, success, meta } = useSelector(
    (state) => state.roles
  );

  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: '',
  });

  const [selectedRole, setSelectedRole] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Use ref to track initial mount
  const initialMount = useRef(true);
  // Use ref to track if filters have changed
  const filtersRef = useRef(filters);

  // Styles object
  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    createButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      transition: 'background-color 0.2s',
    },
    createButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    searchContainer: {
      marginBottom: '1rem',
    },
    tableContainer: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    tableHeader: {
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
    },
    tableHeaderCell: {
      padding: '0.75rem 1.5rem',
      textAlign: 'left',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
      borderBottom: '1px solid #e5e7eb',
    },
    tableHeaderCellRight: {
      padding: '0.75rem 1.5rem',
      textAlign: 'right',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
      borderBottom: '1px solid #e5e7eb',
    },
    tableRow: {
      borderBottom: '1px solid #e5e7eb',
      transition: 'background-color 0.2s',
    },
    tableRowHover: {
      backgroundColor: '#f9fafb',
    },
    tableCell: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
    },
    tableCellRight: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
      textAlign: 'right',
    },
    roleName: {
      fontWeight: 500,
      color: '#111827',
    },
    description: {
      maxWidth: '20rem',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
    userCount: {
      display: 'inline-flex',
      padding: '0.25rem 0.5rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      backgroundColor: '#dbeafe',
      color: '#1e40af',
      borderRadius: '9999px',
    },
    permissionsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.25rem',
    },
    permissionBadge: {
      padding: '0.25rem 0.5rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      backgroundColor: '#f3f4f6',
      color: '#374151',
      borderRadius: '0.25rem',
    },
    actionsContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '0.5rem',
    },
    actionButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.25rem',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      transition: 'color 0.2s',
      borderRadius: '0.375rem',
    },
    actionButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    viewButton: {
      color: '#2563eb',
    },
    viewButtonHover: {
      color: '#1d4ed8',
    },
    editButton: {
      color: '#4f46e5',
    },
    editButtonHover: {
      color: '#4338ca',
    },
    permissionsButton: {
      color: '#059669',
    },
    permissionsButtonHover: {
      color: '#047857',
    },
    deleteButton: {
      color: '#dc2626',
    },
    deleteButtonHover: {
      color: '#b91c1c',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1.5rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
  };

  // Memoize fetch function
  const fetchRolesData = useCallback(() => {
    dispatch(fetchRoles(filters));
  }, [dispatch, filters]);

  // Only fetch when filters actually change
  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      fetchRolesData();
    } else {
      if (JSON.stringify(filtersRef.current) !== JSON.stringify(filters)) {
        filtersRef.current = filters;
        fetchRolesData();
      }
    }
  }, [fetchRolesData, filters]);

  // Cleanup function
  useEffect(() => {
    return () => {
      dispatch(clearRoleError());
      dispatch(clearRoleSuccess());
    };
  }, [dispatch]);

  const handleSearch = useCallback((searchTerm) => {
    setFilters((prev) => ({
      ...prev,
      search: searchTerm,
      page: 1,
    }));
  }, []);

  const handlePageChange = useCallback((page) => {
    setFilters((prev) => ({
      ...prev,
      page,
    }));
  }, []);

  const handleDeleteClick = useCallback((role) => {
    setSelectedRole(role);
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      await dispatch(deleteRole(selectedRole.id));
      setShowDeleteConfirm(false);
      setSelectedRole(null);
      fetchRolesData();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, selectedRole, fetchRolesData]);

  const handleCloseDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setSelectedRole(null);
  }, []);

  const handleClearError = useCallback(() => {
    dispatch(clearRoleError());
  }, [dispatch]);

  const handleClearSuccess = useCallback(() => {
    dispatch(clearRoleSuccess());
  }, [dispatch]);

  if (loading && !roles.length) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Gestion des rôles</h1>
        <Link
          to="/admin/roles/form"
          style={styles.createButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = styles.createButtonHover.backgroundColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = styles.createButton.backgroundColor;
          }}
        >
          Créer un nouveau rôle
        </Link>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={handleClearError}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={handleClearSuccess}
        />
      )}

      <div style={styles.searchContainer}>
        <SearchBar 
          onSearch={handleSearch} 
          placeholder="Rechercher des rôles par nom ou description..." 
          fullWidth
        />
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead style={styles.tableHeader}>
            <tr>
              <th style={styles.tableHeaderCell}>Nom du rôle</th>
              <th style={styles.tableHeaderCell}>Description</th>
              <th style={styles.tableHeaderCell}>Utilisateurs</th>
              <th style={styles.tableHeaderCell}>Permissions</th>
              <th style={styles.tableHeaderCellRight}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((role) => (
              <tr 
                key={role.id} 
                style={styles.tableRow}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.tableRowHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <td style={styles.tableCell}>
                  <span style={styles.roleName}>{role.name}</span>
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.description}>
                    {role.description || '—'}
                  </div>
                </td>
                <td style={styles.tableCell}>
                  <span style={styles.userCount}>
                    {role._count?.userRoles || 0} utilisateurs
                  </span>
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.permissionsContainer}>
                    {role.rolePermissions?.slice(0, 3).map((rp) => (
                      <span
                        key={rp.permission.id}
                        style={styles.permissionBadge}
                        title={rp.permission.description}
                      >
                        {rp.permission.name}
                      </span>
                    ))}
                    {role.rolePermissions?.length > 3 && (
                      <span style={styles.permissionBadge}>
                        +{role.rolePermissions.length - 3}
                      </span>
                    )}
                  </div>
                </td>
                <td style={styles.tableCellRight}>
                  <div style={styles.actionsContainer}>
                    <Link
                      to={`/admin/roles/${role.id}/details`}
                      style={{
                        ...styles.actionButton,
                        ...styles.viewButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.viewButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.viewButton.color;
                      }}
                      title="View Details"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </Link>

                    <Link
                      to={`/admin/roles/${role.id}/edit`}
                      style={{
                        ...styles.actionButton,
                        ...styles.editButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.editButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.editButton.color;
                      }}
                      title="Edit Role"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Link>

                    <Link
                      to={`/admin/roles/${role.id}/permissions`}
                      style={{
                        ...styles.actionButton,
                        ...styles.permissionsButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.permissionsButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.permissionsButton.color;
                      }}
                      title="Manage Permissions"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </Link>

                    <button
                      onClick={() => handleDeleteClick(role)}
                      disabled={role._count?.userRoles > 0}
                      style={{
                        ...styles.actionButton,
                        ...styles.deleteButton,
                        ...(role._count?.userRoles > 0 ? styles.actionButtonDisabled : {}),
                      }}
                      onMouseEnter={(e) => {
                        if (role._count?.userRoles === 0) {
                          e.currentTarget.style.color = styles.deleteButtonHover.color;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (role._count?.userRoles === 0) {
                          e.currentTarget.style.color = styles.deleteButton.color;
                        }
                      }}
                      title={role._count?.userRoles > 0 ? 'Impossible de supprimer un rôle avec des utilisateurs assignés' : 'Supprimer le rôle'}
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {roles.length === 0 && !loading && (
          <div style={styles.emptyState}>
            Aucun rôle trouvé correspondant à vos critères
          </div>
        )}

        <Pagination
          currentPage={meta.page}
          totalPages={meta.totalPages}
          onPageChange={handlePageChange}
        />
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={handleCloseDelete}
        onConfirm={handleDeleteConfirm}
        title="Supprimer le rôle"
        message={`Êtes-vous sûr de vouloir supprimer le rôle "${selectedRole?.name}" ? Cette action ne peut être annulée.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default RoleList;